import type { Session } from "@supabase/supabase-js";
import { lazy, Suspense, useEffect, useRef, useState } from "react";

import { getFriendAccessStatus, clearRememberedFriendAccessCode, setAdminPlanPreview, type FriendAccessStatus } from "../access/friendAccess";
import { recordPrecisePlateActivity, recordStarterTourVisit, starterTourFeatureForPath } from "../admin/usage";
import {
  AccountDeletedPage,
  DeleteAccountPage,
  LegalConsentPage,
  MedicalDisclaimerPage,
  PrivacyPage,
  PublicPageShell,
  SupportPage,
  TermsPage,
} from "../pages/LaunchSafetyPages";
import { LEGAL_VERSION, acceptCurrentLegal, ensurePrecisePlateAccount, hasCurrentLegalAcceptance, type AccountStatus } from "../legal/account";
import { supabase } from "../supabase";
import { isFinanceAdmin } from "../finance/finance";
import { AppShell, EmptyState, LaunchScreen, LoadingPanel } from "./AppShell";
import { AuthPage } from "./AuthPage";
import { AppLink, navigate, useNavigationKey, usePathname } from "./router";
import { clearStalePageRetrySlot, consumeStalePageSplashBypass, takeStalePageRetrySlot } from "./stalePageReload";

const DashboardPage = lazy(() => import("../pages/DashboardPage").then((module) => ({ default: module.DashboardPage })));
const CalendarPage = lazy(() => import("../pages/CalendarPage").then((module) => ({ default: module.CalendarPage })));
const GroceryPage = lazy(() => import("../pages/GroceryPage").then((module) => ({ default: module.GroceryPage })));
const SavedMealsPage = lazy(() => import("../pages/SavedMealsPage").then((module) => ({ default: module.SavedMealsPage })));
const GeneratorPage = lazy(() => import("../pages/GeneratorPage").then((module) => ({ default: module.GeneratorPage })));
const MealBuilderPage = lazy(() => import("../pages/MealBuilderPage").then((module) => ({ default: module.MealBuilderPage })));
const PantryPage = lazy(() => import("../pages/PantryPage").then((module) => ({ default: module.PantryPage })));
const ProfilePage = lazy(() => import("../pages/ProfilePage").then((module) => ({ default: module.ProfilePage })));
const HealthTrackingPage = lazy(() => import("../pages/TrackingPages").then((module) => ({ default: module.HealthTrackingPage })));
const HydrationPage = lazy(() => import("../pages/TrackingPages").then((module) => ({ default: module.HydrationPage })));
const HealthMeasurementsPage = lazy(() => import("../pages/TrackingPages").then((module) => ({ default: module.HealthMeasurementsPage })));
const CgmPage = lazy(() => import("../pages/CgmPage").then((module) => ({ default: module.CgmPage })));
const SettingsPage = lazy(() => import("../pages/SupportPages").then((module) => ({ default: module.SettingsPage })));
const CancelSubscriptionPage = lazy(() => import("../pages/SupportPages").then((module) => ({ default: module.CancelSubscriptionPage })));
const GuidePage = lazy(() => import("../pages/SupportPages").then((module) => ({ default: module.GuidePage })));
const FaqPage = lazy(() => import("../pages/SupportPages").then((module) => ({ default: module.FaqPage })));
const PricingPage = lazy(() => import("../pages/SupportPages").then((module) => ({ default: module.PricingPage })));
const ResearchPage = lazy(() => import("../pages/ResearchPage").then((module) => ({ default: module.ResearchPage })));
const FinanceAdminPage = lazy(() => import("../pages/FinanceAdminPage").then((module) => ({ default: module.FinanceAdminPage })));
const InstallPage = lazy(() => import("../pages/InstallPage").then((module) => ({ default: module.InstallPage })));

const FREE_ACCESS_FALLBACK: FriendAccessStatus = {
  fullAccess: false,
  source: "free",
  accessCodeActive: false,
  planCode: "free",
  canSaveMeals: false,
  canUseCalendar: false,
  canManageHousehold: false,
  pantryLimit: 10,
  pantryCount: 0,
  generatorUsesLimit: 5,
  generatorUsesUsed: 0,
  generatorUsesRemaining: 5,
  allowedPlanningModes: ["single_meal"],
};

const accountRetryDelaysMs = [0, 500, 1_250];

function LegacyWeightLogRedirect() {
  useEffect(() => { navigate(`/health-measurements${window.location.search}`, true); }, []);
  return <LoadingPanel label="Opening measurements..." />;
}

async function openPrecisePlateAccount(): Promise<AccountStatus> {
  let lastError: unknown;
  for (let attempt = 0; attempt < accountRetryDelaysMs.length; attempt += 1) {
    if (accountRetryDelaysMs[attempt]) {
      await new Promise((resolve) => window.setTimeout(resolve, accountRetryDelaysMs[attempt]));
    }
    try {
      return await ensurePrecisePlateAccount();
    } catch (caught) {
      lastError = caught;
      if (attempt === 0) {
        try {
          const { error } = await supabase.auth.refreshSession();
          if (error) lastError = error;
        } catch (refreshError) {
          lastError = refreshError;
        }
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error("Precise Plate could not open this account.");
}

export function App() {
  const pathname = usePathname();
  const navigationKey = useNavigationKey();
  const startupRedirectHandled = useRef(false);
  const [splashComplete, setSplashComplete] = useState(() => consumeStalePageSplashBypass());
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [accountStatus, setAccountStatus] = useState<AccountStatus | "loading" | "error">("loading");
  const [legalAccepted, setLegalAccepted] = useState<boolean | undefined>(undefined);
  const [financeAdmin, setFinanceAdmin] = useState<boolean | undefined>(undefined);
  const [planAccess, setPlanAccess] = useState<FriendAccessStatus | undefined>(undefined);
  const [accessNotice, setAccessNotice] = useState<{ kind: "success" | "error"; message: string } | null>(null);
  const [accountCheckAttempt, setAccountCheckAttempt] = useState(0);
  const sessionUserId = session?.user.id;

  useEffect(() => {
    const handlePreloadError = (event: Event) => {
      event.preventDefault();
      if (takeStalePageRetrySlot()) window.location.reload();
    };
    window.addEventListener("vite:preloadError", handlePreloadError);
    const stableTimer = window.setTimeout(clearStalePageRetrySlot, 10_000);
    return () => {
      window.removeEventListener("vite:preloadError", handlePreloadError);
      window.clearTimeout(stableTimer);
    };
  }, []);

  useEffect(() => {
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    });
    return () => {
      window.cancelAnimationFrame(frame);
      window.history.scrollRestoration = previousRestoration;
    };
  }, [pathname]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSplashComplete(true), 5000);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => setSession(nextSession));
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!sessionUserId) return;
    const sessionKey = `precise-plate-login-counted:${sessionUserId}`;
    if (window.sessionStorage.getItem(sessionKey)) return;
    const storageKey = `precise-plate-login-count:${sessionUserId}`;
    const current = Number(window.localStorage.getItem(storageKey) ?? "0");
    window.localStorage.setItem(storageKey, String(Number.isFinite(current) ? current + 1 : 1));
    window.sessionStorage.setItem(sessionKey, "1");
  }, [sessionUserId]);

  useEffect(() => {
    clearRememberedFriendAccessCode();
  }, []);

  useEffect(() => {
    if (!sessionUserId || accountStatus !== "active" || (legalAccepted !== true && financeAdmin !== true)) return;
    void recordPrecisePlateActivity().catch(() => undefined);
  }, [accountStatus, financeAdmin, legalAccepted, sessionUserId]);

  useEffect(() => {
    if (!sessionUserId || accountStatus !== "active" || (legalAccepted !== true && financeAdmin !== true)) return;
    const feature = starterTourFeatureForPath(pathname);
    if (!feature) return;
    void recordStarterTourVisit(feature, sessionUserId).catch(() => undefined);
  }, [accountStatus, financeAdmin, legalAccepted, pathname, sessionUserId]);

  useEffect(() => {
    if (session === undefined || startupRedirectHandled.current) return;
    startupRedirectHandled.current = true;
    const destination = startupDestination(pathname, Boolean(session));
    if (destination) navigate(destination, true);
  }, [pathname, session]);

  useEffect(() => {
    let active = true;
    if (!session) {
      setAccountStatus("loading");
      setLegalAccepted(undefined);
      setPlanAccess(undefined);
      return () => { active = false; };
    }
    setAccountStatus("loading");
    setLegalAccepted(undefined);
    void (async () => {
      let status: AccountStatus;
      try {
        status = await openPrecisePlateAccount();
      } catch (caught) {
        console.error("Precise Plate account check failed", caught);
        if (active) { setAccountStatus("error"); setLegalAccepted(false); }
        return;
      }
      if (!active) return;
      setAccountStatus(status);
      if (status === "deleted") {
        await supabase.auth.signOut({ scope: "local" });
        if (active) navigate("/account-deleted", true);
        return;
      }
      if (status !== "active") { setLegalAccepted(false); return; }
      try {
        let accepted = await hasCurrentLegalAcceptance(session.user.id);
        const metadata = session.user.user_metadata ?? {};
        const signupAccepted = metadata.terms_version === LEGAL_VERSION
          && metadata.privacy_version === LEGAL_VERSION
          && metadata.medical_disclaimer_version === LEGAL_VERSION
          && typeof metadata.legal_accepted_at === "string";
        if (!accepted && signupAccepted) {
          await acceptCurrentLegal("signup");
          accepted = true;
        }
        if (active) setLegalAccepted(accepted);
      } catch {
        if (active) setLegalAccepted(false);
      }
    })();
    return () => { active = false; };
  }, [accountCheckAttempt, sessionUserId]);

  useEffect(() => {
    let active = true;
    if (!sessionUserId || accountStatus !== "active") {
      setPlanAccess(undefined);
      return () => { active = false; };
    }
    void getFriendAccessStatus()
      .then((status) => { if (active) setPlanAccess(status); })
      .catch(() => { if (active) setPlanAccess((current) => current ?? FREE_ACCESS_FALLBACK); });
    return () => { active = false; };
  }, [accountStatus, pathname, sessionUserId]);

  useEffect(() => {
    let active = true;
    if (!session) {
      setFinanceAdmin(undefined);
      return () => { active = false; };
    }
    setFinanceAdmin(undefined);
    void isFinanceAdmin()
      .then((value) => { if (active) setFinanceAdmin(value); })
      .catch(() => { if (active) setFinanceAdmin(false); });
    return () => { active = false; };
  }, [sessionUserId]);

  if (!splashComplete || session === undefined) return <LaunchScreen />;
  const publicPage = pathname === "/privacy" ? <PrivacyPage />
    : pathname === "/terms" ? <TermsPage />
      : pathname === "/medical-disclaimer" ? <MedicalDisclaimerPage />
        : pathname === "/why-precise-plate" ? <Suspense fallback={<LoadingPanel label="Opening the research library..." />}><ResearchPage /></Suspense>
          : pathname === "/install" ? <Suspense fallback={<LoadingPanel label="Opening installation options..." />}><InstallPage /></Suspense>
          : pathname === "/pricing" ? <Suspense fallback={<LoadingPanel label="Opening pricing..." />}><PricingPage /></Suspense>
        : pathname === "/support" ? <SupportPage user={session?.user ?? null} />
          : pathname === "/delete-account" ? <DeleteAccountPage user={session?.user ?? null} />
            : pathname === "/account-deleted" ? <AccountDeletedPage />
              : null;
  const canUseAuthenticatedShell = Boolean(session)
    && accountStatus === "active"
    && (legalAccepted === true || financeAdmin === true)
    && pathname !== "/account-deleted";
  if (publicPage) return canUseAuthenticatedShell && session ? <AppShell pathname={pathname} email={session.user.email ?? "Precise Plate user"} financeAdmin={financeAdmin === true} access={planAccess} onAdministratorPlanToggle={toggleAdministratorPlan}>{publicPage}</AppShell> : <PublicPageShell showSignIn={!session}>{publicPage}</PublicPageShell>;
  const authMode = pathname === "/signup" ? "signup" : pathname === "/forgot-password" ? "forgot" : pathname === "/reset-password" ? "reset" : pathname === "/login" ? "login" : null;
  if (authMode === "reset") return <AuthPage mode="reset" />;
  if (!session) return <AuthPage mode={authMode ?? "login"} />;
  if (authMode) { navigate("/dashboard", true); return null; }
  async function signOutFromAccountError() {
    await supabase.auth.signOut({ scope: "local" });
    setSession(null);
    navigate("/login", true);
  }

  if (accountStatus === "loading") return <LaunchScreen label="Checking your account..." />;
  if (accountStatus === "error" || accountStatus === "missing") return <PublicPageShell showSignIn={false}><section className="consent-panel"><h1>Your Precise Plate account needs attention</h1><p>The app could not open your account data safely. Try again first. If it still does not open, log out and sign in again before contacting support.</p><div className="account-actions"><button className="button primary" type="button" onClick={() => setAccountCheckAttempt((attempt) => attempt + 1)}>Try again</button><button className="button secondary" type="button" onClick={() => void signOutFromAccountError()}>Log out</button><AppLink href="/support" className="button secondary">Contact support</AppLink></div></section></PublicPageShell>;
  if (financeAdmin === undefined) return <LaunchScreen label="Checking your account..." />;
  if (legalAccepted === undefined || planAccess === undefined) return <LaunchScreen label="Checking your account..." />;
  if (!legalAccepted && !financeAdmin) return <LegalConsentPage email={session.user.email ?? "this account"} source="in_app" onAccepted={() => setLegalAccepted(true)} />;

  async function toggleAdministratorPlan() {
    if (!financeAdmin || !planAccess) return;
    const nextPlan = planAccess.fullAccess ? "free" : "household";
    try {
      await setAdminPlanPreview(nextPlan);
      const nextAccess = await getFriendAccessStatus();
      setPlanAccess(nextAccess);
      setAccessNotice({ kind: "success", message: `Administrator preview changed to the ${nextAccess.fullAccess ? "Household" : "Free"} plan. Billing and stored data were not changed.` });
    } catch (caught) {
      setAccessNotice({ kind: "error", message: caught instanceof Error ? caught.message : "The administrator plan preview could not be changed." });
    }
  }

  const user = session.user;
  const page = pathname === "/dashboard" ? <DashboardPage user={user} />
    : pathname === "/generator" ? <GeneratorPage key={`generator-${navigationKey}`} user={user} access={planAccess} />
      : pathname === "/calendar" ? <CalendarPage user={user} access={planAccess} />
        : pathname === "/saved-meals" ? <SavedMealsPage user={user} access={planAccess} />
          : pathname === "/pantry" ? <PantryPage user={user} access={planAccess} />
          : pathname === "/meal-builder" ? <MealBuilderPage user={user} access={planAccess} />
            : pathname === "/grocery-lists" ? <GroceryPage user={user} />
            : pathname === "/profile" ? <ProfilePage user={user} access={planAccess} />
              : pathname === "/health-tracking" ? <HealthTrackingPage user={user} />
                : pathname === "/hydration" ? <HydrationPage user={user} />
                  : pathname === "/weight-log" ? <LegacyWeightLogRedirect />
                    : pathname === "/health-measurements" ? <HealthMeasurementsPage user={user} />
                      : pathname === "/glucose-trends" ? <CgmPage user={user} />
                    : pathname === "/settings" ? <SettingsPage user={user} />
                    : pathname === "/billing/cancel" ? <CancelSubscriptionPage user={user} />
                      : pathname === "/guide" ? <GuidePage />
                        : pathname === "/faqs" ? <FaqPage />
                        : pathname === "/pricing" ? <PricingPage />
                          : pathname === "/admin/finance" ? financeAdmin === undefined
                            ? <LoadingPanel label="Checking finance access..." />
                            : financeAdmin
                              ? <FinanceAdminPage />
                              : <EmptyState title="Finance access is restricted" body="This area is available only to the Precise Plate owner." action={<AppLink href="/dashboard" className="button primary">Return to dashboard</AppLink>} />
                            : <EmptyState title="Page not found" body="That page does not exist in Precise Plate." action={<AppLink href="/dashboard" className="button primary">Return to dashboard</AppLink>} />;

  return <AppShell pathname={pathname} email={user.email ?? "Precise Plate user"} financeAdmin={financeAdmin === true} access={planAccess} onAdministratorPlanToggle={toggleAdministratorPlan}>{accessNotice ? <div className={`notice ${accessNotice.kind}`} role={accessNotice.kind === "error" ? "alert" : "status"}><span>{accessNotice.message}</span><button className="notice-dismiss" type="button" onClick={() => setAccessNotice(null)}>Dismiss</button></div> : null}<Suspense fallback={<LoadingPanel label="Opening this page..." />}>{page}</Suspense></AppShell>;
}

const publicStartupPaths = new Set([
  "/privacy",
  "/terms",
  "/medical-disclaimer",
  "/why-precise-plate",
  "/install",
  "/pricing",
  "/support",
  "/delete-account",
  "/billing/cancel",
  "/account-deleted",
  "/reset-password",
]);

const authStartupPaths = new Set([
  "/login",
  "/signup",
  "/forgot-password",
  "/auth/callback",
]);

const privateStartupPaths = new Set([
  "/dashboard",
  "/generator",
  "/calendar",
  "/saved-meals",
  "/pantry",
  "/meal-builder",
  "/grocery-lists",
  "/profile",
  "/health-tracking",
  "/hydration",
  "/weight-log",
  "/health-measurements",
  "/glucose-trends",
  "/settings",
  "/admin/finance",
]);

export function startupDestination(pathname: string, signedIn: boolean): string | null {
  if (signedIn) {
    if (pathname === "/" || authStartupPaths.has(pathname)) return "/dashboard";
    return publicStartupPaths.has(pathname) || privateStartupPaths.has(pathname) ? null : "/dashboard";
  }
  return pathname === "/" || pathname === "/auth/callback" ? "/login" : null;
}
